'use client';

import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { ArrowRight, X } from 'lucide-react';

/**
 * "Read about the series": an outer scroll track with the artworks at exactly
 * one viewport (the grid keeps its own infinite scroll inside) and the series
 * info below in normal flow. Opening scrolls the track down one viewport, the
 * same button or plain scrolling brings the artworks back, and the fixed
 * search bar is untouched because no transforms are involved.
 *
 * Every handler resolves the track by id at call time instead of via a ref.
 * The grid subtree re-renders itself out of a loading state after mount, and
 * a captured ref proved unreliable across that swap; the live DOM lookup is
 * immune. The component also broadcasts its open state so the footer button
 * can show open or close.
 */
export const ABOUT_SERIES_SYNC_EVENT = 'about-series-sync';
export const ABOUT_SERIES_STATE_EVENT = 'about-series-state';
const TRACK_ID = 'about-series-track';
const HASH = '#about-the-series';

const getTrack = () => document.getElementById(TRACK_ID);

export function toggleAboutSeries() {
  window.dispatchEvent(new Event(ABOUT_SERIES_SYNC_EVENT));
}

function broadcast(open: boolean) {
  window.dispatchEvent(new CustomEvent(ABOUT_SERIES_STATE_EVENT, { detail: { open } }));
}

export default function AboutSeriesReveal({
  name,
  grid,
  about,
  caseStudyHref,
}: {
  name: string;
  grid: ReactNode;
  about: ReactNode;
  /** Set only for a series with a case study on this site. */
  caseStudyHref?: string;
}) {
  const [open, setOpen] = useState(false);
  const openRef = useRef(false);

  const scrollTo = useCallback((target: 'about' | 'grid' | HTMLElement) => {
    const track = getTrack();
    if (!track) return;
    /*
     * Hand-rolled animation instead of behavior: 'smooth'. Native smooth
     * scrolling on this element was silently swallowed in production while
     * instant scrollTop writes worked, so the animation drives scrollTop
     * directly and cannot be cancelled from outside.
     */
    const from = track.scrollTop;
    const section = document.getElementById('about-the-series');
    const to =
      target instanceof HTMLElement
        ? track.scrollTop + target.getBoundingClientRect().top - track.getBoundingClientRect().top
        : target === 'about'
          ? section ? section.offsetTop : track.clientHeight
          : 0;
    const duration = 550;
    const start = performance.now();
    const ease = (t: number) => (t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2);
    const step = (now: number) => {
      const p = Math.min(1, (now - start) / duration);
      track.scrollTop = from + (to - from) * ease(p);
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, []);

  // The footer button fires the sync event; toggle on the actual position.
  useEffect(() => {
    const onToggle = () => {
      const track = getTrack();
      if (!track) return;
      const section = document.getElementById('about-the-series');
      const threshold = (section ? section.offsetTop : track.clientHeight) / 2;
      const isOpen = track.scrollTop > threshold;
      scrollTo(isOpen ? 'grid' : 'about');
    };
    window.addEventListener(ABOUT_SERIES_SYNC_EVENT, onToggle);
    return () => window.removeEventListener(ABOUT_SERIES_SYNC_EVENT, onToggle);
  }, [scrollTo]);

  // Track the real position, including manual scrolls, and tell the footer.
  useEffect(() => {
    const track = getTrack();
    if (!track) return undefined;
    const onScroll = () => {
      const section = document.getElementById('about-the-series');
      const threshold = (section ? section.offsetTop : track.clientHeight) / 2;
      const isOpen = track.scrollTop > threshold;
      // Broadcast outside the state updater: React runs updaters while rendering this
      // component, and the footer's listener sets its own state.
      if (openRef.current === isOpen) return;
      openRef.current = isOpen;
      setOpen(isOpen);
      broadcast(isOpen);
    };
    track.addEventListener('scroll', onScroll, { passive: true });
    return () => track.removeEventListener('scroll', onScroll);
  }, []);

  // Deep link: /catalog/collections/x#about-the-series opens on load, and a hash naming an
  // element inside the section (e.g. #raffle) scrolls to it once it has rendered.
  useEffect(() => {
    const hash = window.location.hash;
    if (hash === HASH) {
      requestAnimationFrame(() => scrollTo('about'));
    } else if (hash.length > 1) {
      let tries = 0;
      const find = () => {
        const el = document.getElementById(decodeURIComponent(hash.slice(1)));
        const track = getTrack();
        if (el && track?.contains(el)) scrollTo(el);
        else if (++tries < 40) setTimeout(find, 100);
      };
      find();
    }
  }, [scrollTo]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') scrollTo('grid');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, scrollTo]);

  return (
    <div id={TRACK_ID} className="h-full overflow-y-auto" style={{ scrollbarWidth: 'none' }}>
      {/* One viewport of artworks; when its inner scroller reaches the end,
          scroll chaining hands the wheel to this track. */}
      <div className="h-full">{grid}</div>

      <section
        id="about-the-series"
        role="region"
        aria-label={`About the series ${name}`}
        className="min-h-full bg-white"
      >
        <div className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 bg-white/90 px-[15px] py-3 backdrop-blur">
          <h2 className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
            [ ABOUT THE SERIES: {name} ]
          </h2>
          <div className="flex items-center gap-4">
            {caseStudyHref ? (
              <Link
                href={caseStudyHref}
                className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-black transition-colors hover:text-zinc-500"
              >
                Read the case study <ArrowRight size={12} />
              </Link>
            ) : null}
            <button
              type="button"
              onClick={() => scrollTo('grid')}
              className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-zinc-500 transition-colors hover:text-black"
            >
              Back to artworks <X size={12} />
            </button>
          </div>
        </div>
        {about}
      </section>
    </div>
  );
}

/**
 * The footer that carries "read about the series" is desktop-only, so on phones the
 * collection header carries the same actions.
 */
export function AboutSeriesPhoneActions({ caseStudyHref }: { caseStudyHref?: string }) {
  return (
    <div className="mt-3 flex flex-wrap gap-2 lg:hidden">
      <button type="button" onClick={toggleAboutSeries} className="nav-button">
        Read about the series {'↓'}
      </button>
      {caseStudyHref ? (
        <Link href={caseStudyHref} className="nav-button">
          Read the case study
        </Link>
      ) : null}
    </div>
  );
}
